import type { JohnDeereMapLayerExtent, Polygon } from './types';

// Runtime boundary shape is GeoJSON-style Position[][] (rings of [lng, lat]
// tuples), not the JTS Coordinate[] that the generated Polygon type claims.
// The same mismatch is handled by components/Map/hooks/useDTO.convertFromDTO
// via a cast — following the same pattern here. If envelopeInternal is
// serialized through on a different ingest path, we still use it first.
export const computeExtent = (polygon: Polygon): JohnDeereMapLayerExtent => {
    const envelope = polygon.envelopeInternal;

    if (
        envelope &&
        typeof envelope.minX === 'number' &&
        typeof envelope.minY === 'number' &&
        typeof envelope.maxX === 'number' &&
        typeof envelope.maxY === 'number'
    ) {
        return {
            maximumLatitude: envelope.maxY,
            maximumLongitude: envelope.maxX,
            minimumLatitude: envelope.minY,
            minimumLongitude: envelope.minX
        };
    }

    const rings = (polygon.coordinates ?? []) as unknown as number[][][];

    let minimumLongitude = Number.POSITIVE_INFINITY;
    let maximumLongitude = Number.NEGATIVE_INFINITY;
    let minimumLatitude = Number.POSITIVE_INFINITY;
    let maximumLatitude = Number.NEGATIVE_INFINITY;
    let validPointCount = 0;

    for (const ring of rings) {
        if (!Array.isArray(ring)) {
            continue;
        }

        for (const point of ring) {
            if (!Array.isArray(point) || point.length < 2) {
                continue;
            }

            const longitude = point[0];
            const latitude = point[1];

            if (typeof longitude !== 'number' || typeof latitude !== 'number') {
                continue;
            }

            if (longitude < minimumLongitude) {
                minimumLongitude = longitude;
            }

            if (longitude > maximumLongitude) {
                maximumLongitude = longitude;
            }

            if (latitude < minimumLatitude) {
                minimumLatitude = latitude;
            }

            if (latitude > maximumLatitude) {
                maximumLatitude = latitude;
            }

            validPointCount += 1;
        }
    }

    if (validPointCount === 0) {
        throw new Error('Cannot compute extent: polygon has no coordinates');
    }

    return {
        maximumLatitude,
        maximumLongitude,
        minimumLatitude,
        minimumLongitude
    };
};
